"use client"
import Button from '@/components/ui/Button'
import * as Dialog from '@radix-ui/react-dialog'
import * as Label from '@radix-ui/react-label'
import { fetchJson, HttpError } from '@/lib/fetch-json'
import { AlertCircle, Check, Eye, EyeOff, Lock, RefreshCw, Shield, User, X } from 'lucide-react'
import { useSession } from 'next-auth/react'
import React, { useCallback, useMemo, useState } from 'react'
import { AdminOnly, useRBAC } from './RBACGuard'

interface User {
  id: string
  name: string
  email: string
  role: 'Admin' | 'Editor' | 'Author'
}

interface UserCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onUserCreated: () => void
  editingUser?: User | null
}

interface FormData {
  name: string
  email: string
  password: string
  confirmPassword: string
  role: string
}

interface FormErrors {
  name?: string
  email?: string
  password?: string
  confirmPassword?: string
  role?: string
  general?: string
}

const roleTabs = [
  {
    value: 'Author',
    label: 'Author',
    description: 'Create blog posts and drafts',
    icon: User,
    color: 'blue',
    domain: 'author.com'
  },
  {
    value: 'Editor',
    label: 'Editor',
    description: 'Create, edit, and publish content',
    icon: Shield,
    color: 'green',
    domain: 'editor.com'
  }
]

export default function UserCreateModal({ isOpen, onClose, onUserCreated, editingUser }: UserCreateModalProps) {
  const isEditMode = !!editingUser
  const { data: session } = useSession()
  const { } = useRBAC()

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'Author' // Default to Author role
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Password verification states
  const [isPasswordVerified, setIsPasswordVerified] = useState(false)
  const [adminPassword, setAdminPassword] = useState('')
  const [showVerification, setShowVerification] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)

  // Check if admin is editing themselves
  const isEditingSelf = useMemo(() =>
    isEditMode && session?.user?.email === editingUser?.email,
    [isEditMode, session?.user?.email, editingUser?.email]
  )

  // Check if we should hide role selection
  const shouldHideRoleSelection = useMemo(() => {
    // Hide role selection only when editing an existing admin user
    // When creating new users, always show role selection
    // When editing non-admin users, show role selection
    // When editing admin users, hide role selection
    const editingUserIsAdmin = isEditMode && editingUser?.role === 'Admin'

    return editingUserIsAdmin
  }, [isEditMode, editingUser?.role])

  // Password verification function
  const verifyAdminPassword = useCallback(async () => {
    if (!adminPassword.trim()) {
      setErrors({ general: 'Please enter your current password' })
      return
    }

    setIsVerifying(true)
    setErrors({})

    try {
      await fetchJson('/api/auth/verify-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      })

      setIsPasswordVerified(true)
      setShowVerification(false)
      setAdminPassword('')
      // Clear confirm password when editing self to avoid confusion
      if (isEditingSelf) {
        setFormData(prev => ({ ...prev, confirmPassword: '' }))
      }
    } catch {
      const errorMessage = 'Password verification failed. Please try again.'

      setErrors({ general: errorMessage })
    } finally {
      setIsVerifying(false)
    }
  }, [adminPassword, isEditingSelf])

  // Reset verification state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
       
      setIsPasswordVerified(false)
      setAdminPassword('')
      setShowVerification(false)
    }
  }, [isOpen, isEditMode, isEditingSelf])

  // Clear admin password when verification modal opens
  React.useEffect(() => {
    if (showVerification) {
       
      setAdminPassword('')
    }
  }, [showVerification])

  // Memoized password strength checker
  const passwordStrength = React.useMemo(() => {
    if (!formData.password) return null

    const checks = {
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      lowercase: /[a-z]/.test(formData.password),
      number: /\d/.test(formData.password),
    }

    const passedChecks = Object.values(checks).filter(Boolean).length
    const strength = passedChecks === 0 ? 'Very Weak' :
      passedChecks === 1 ? 'Weak' :
        passedChecks === 2 ? 'Fair' :
          passedChecks === 3 ? 'Good' : 'Strong'

    return { checks, strength, passedChecks }
  }, [formData.password])

  // Generate secure random password
  const generateSecurePassword = useCallback(() => {
    // Check if verification is required
    if (isEditMode && !isPasswordVerified) {
      setShowVerification(true)
      return
    }

    try {
      const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const lowercase = 'abcdefghijklmnopqrstuvwxyz'
      const numbers = '0123456789'
      const symbols = '!@#$%^&*'

      // Create an array to build password characters
      const passwordChars = []

      // Ensure at least one of each required character type
      passwordChars.push(
        uppercase[Math.floor(Math.random() * uppercase.length)],
        lowercase[Math.floor(Math.random() * lowercase.length)],
        numbers[Math.floor(Math.random() * numbers.length)],
        symbols[Math.floor(Math.random() * symbols.length)]
      )

      // Fill remaining length with random characters from all types
      const allChars = uppercase + lowercase + numbers + symbols
      for (let i = passwordChars.length; i < 12; i++) {
        passwordChars.push(allChars[Math.floor(Math.random() * allChars.length)])
      }

      // Fisher-Yates shuffle for better randomization
      for (let i = passwordChars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
          ;[passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]]
      }

      const password = passwordChars.join('')

      setFormData(prev => ({ ...prev, password, confirmPassword: password }))
    } catch {
      // Fallback to a cryptographically secure password
      const fallbackPassword = 'SecurePass123!'
      setFormData(prev => ({ ...prev, password: fallbackPassword, confirmPassword: fallbackPassword }))
    }
  }, [isEditMode, isPasswordVerified])

  // Reset form when modal opens or editing user changes
  React.useEffect(() => {
    if (isOpen) {
      try {
        if (isEditMode && editingUser) {
          // Populate form with existing user data for editing
           
          setFormData({
            name: editingUser.name || '',
            email: editingUser.email || '',
            password: '••••••••', // Show masked password for security
            confirmPassword: isEditingSelf ? '' : '', // Always empty for edit mode
            role: editingUser.role || 'Author'
          })
        } else {
          // Reset form for new user creation
          setFormData({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            role: 'Author' // Default to Author role
          })
        }
        setErrors({})
        setShowPassword(false)
        setShowConfirmPassword(false)
        setIsPasswordVerified(false)
        setAdminPassword('')
        setShowVerification(false)
      } catch {
        setErrors({ general: 'Failed to initialize form. Please try again.' })
      }
    }
  }, [isOpen, isEditMode, editingUser, isEditingSelf])

  // Cleanup effect to prevent memory leaks
  React.useEffect(() => {
    return () => {
      // Cleanup any pending operations
      setErrors({})
    }
  }, [])

  const validateForm = useCallback((): boolean => {
    try {
      const newErrors: FormErrors = {}

      // Name validation
      if (!formData.name || !formData.name.trim()) {
        newErrors.name = 'Name is required'
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Name must be at least 2 characters'
      } else if (formData.name.trim().length > 100) {
        newErrors.name = 'Name must be less than 100 characters'
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'Email is required'
      } else if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address'
      } else {
        // Domain validation based on role
        const emailParts = formData.email.trim().split('@')
        if (emailParts.length !== 2) {
          newErrors.email = 'Invalid email format'
        } else {
          const emailDomain = emailParts[1].toLowerCase()
          // Skip domain validation if role selection is hidden (for admin users)
          if (!shouldHideRoleSelection) {
            if (formData.role === 'Author' && emailDomain !== 'author.com') {
              newErrors.email = 'Authors must use @author.com domain'
            } else if (formData.role === 'Editor' && emailDomain !== 'editor.com') {
              newErrors.email = 'Editors must use @editor.com domain'
            }
          }
        }
      }

      // Password validation (required for new users, optional for edits)
      if (!isEditMode) {
        if (!formData.password) {
          newErrors.password = 'Password is required'
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters'
        } else if (formData.password.length > 128) {
          newErrors.password = 'Password must be less than 128 characters'
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password'
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match'
        }
      } else if (formData.password && formData.password !== '••••••••') {
        // If editing and password is provided (and not the masked placeholder), validate it
        if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters'
        } else if (formData.password.length > 128) {
          newErrors.password = 'Password must be less than 128 characters'
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
        }

        // Skip confirm password validation for self-editing (field is hidden)
        if (!isEditingSelf) {
          if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password'
          } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match'
          }
        }
      }

      // Role validation - skip if role selection is hidden for admin users
      if (!shouldHideRoleSelection && (!formData.role || !['Author', 'Editor'].includes(formData.role))) {
        newErrors.role = 'Please select a valid role'
      }

      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    } catch {
      setErrors({ general: 'Validation failed. Please check your input.' })
      return false
    }
  }, [formData, isEditMode, isEditingSelf, shouldHideRoleSelection])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent double submission
    if (isLoading) {
      return
    }

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      // Validate required data before submission
      if (!formData.name?.trim() || !formData.email?.trim() || !formData.role) {
        throw new Error('Missing required form data')
      }

      const requestData: { name: string; email: string; role: string; password?: string } = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role
      }

      // Only include password if it's provided, valid, and not the masked placeholder (for both create and edit)
      if (formData.password &&
        formData.password.length >= 8 &&
        formData.password !== '••••••••') {
        requestData.password = formData.password
      }

      const url = isEditMode && editingUser?.id
        ? `/api/admin/users/${editingUser.id}`
        : '/api/admin/users'

      if (!url) {
        throw new Error('Invalid request URL')
      }

      await fetchJson(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      })

      onUserCreated()
      onClose()

    } catch (err) {
      const errorMessage =
        err instanceof HttpError
          ? err.message
          : `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`

      setErrors({
        general: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, validateForm, formData, isEditMode, editingUser, onUserCreated, onClose])

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'Author'
    })
    setErrors({})
  }

  const handleClose = useCallback(() => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }, [isLoading, onClose])

  return (
    <AdminOnly>
      <Dialog.Root open={isOpen} onOpenChange={handleClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl z-[101] overflow-hidden border border-gray-200/50">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200/80 flex items-center justify-between flex-shrink-0">
              <div className="flex-1 min-w-0">
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Edit User' : 'Create New User'}
                </Dialog.Title>
                <p className="text-sm text-gray-600 mt-1">
                  {isEditMode ? 'Update user information and permissions' : 'Add a new user to your team'}
                </p>
              </div>
              <Dialog.Close asChild>
                <button
                  className="flex-shrink-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all p-2 rounded-xl"
                  disabled={isLoading}
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>
            
            <div className="p-6 relative overflow-y-auto custom-scrollbar max-h-[calc(90vh-80px)]">

              {/* General Error */}
              {errors.general && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="ml-2 text-sm text-red-700">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Role Selection - Hidden when editing admin users */}
                {!shouldHideRoleSelection && (
                  <div>
                    <Label.Root className="block text-sm font-medium text-gray-700 mb-3">
                      Role
                    </Label.Root>
                    <div className="grid grid-cols-2 gap-3">
                      {roleTabs.map((tab) => {
                        const IconComponent = tab.icon
                        const isSelected = formData.role === tab.value
                        return (
                          <button
                            key={tab.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, role: tab.value }))}
                            disabled={isLoading}
                            className={`p-4 border-2 rounded-xl text-left transition-all duration-200 ${isSelected
                              ? (tab.value === 'Author'
                                ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                : 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm')
                              : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50/30'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isSelected
                                ? (tab.value === 'Author' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600')
                                : 'bg-gray-100 text-gray-500'
                                }`}>
                                <IconComponent className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="font-medium text-sm">{tab.label}</div>
                                <div className={`text-xs mt-1 ${isSelected
                                  ? (tab.value === 'Author' ? 'text-blue-600' : 'text-purple-600')
                                  : 'text-gray-500'
                                  }`}>
                                  {tab.description}
                                </div>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                    {errors.role && (
                      <p className="mt-2 text-sm text-red-600">{errors.role}</p>
                    )}
                  </div>
                )}

                {/* Name Field */}
                <div>
                  <Label.Root htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 ">
                    Full Name
                  </Label.Root>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    placeholder="Enter full name"
                    disabled={isLoading}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <Label.Root htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </Label.Root>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    placeholder={shouldHideRoleSelection ? "username@domain.com" : `username@${formData.role === 'Author' ? 'author.com' : 'editor.com'}`}
                    disabled={isLoading}
                  />
                  {!shouldHideRoleSelection && (
                    <p className="mt-1 text-xs text-gray-500">
                      Must use @{formData.role === 'Author' ? 'author.com' : 'editor.com'} domain
                    </p>
                  )}
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label.Root htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </Label.Root>
                    <div className="flex items-center gap-2">
                      {isEditMode && !isPasswordVerified && (
                        <button
                          type="button"
                          onClick={() => {
                            setAdminPassword(''); // Clear password field before opening modal
                            setShowVerification(true);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
                          disabled={isLoading}
                          aria-label="Verify admin password to modify user password"
                          title="Verify to modify password"
                        >
                          <Lock className="h-3 w-3" />
                          Verify
                        </button>
                      )}
                      {(!isEditMode || isPasswordVerified) && (
                        <button
                          type="button"
                          onClick={generateSecurePassword}
                          className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50"
                          disabled={isLoading}
                          aria-label="Generate a secure random password"
                          title="Generate a secure random password"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Generate
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onFocus={(e) => {
                        // When user focuses on the field, if it's still masked, clear it
                        if (isEditMode && isPasswordVerified && e.target.value === '••••••••') {
                          setFormData(prev => ({ ...prev, password: '' }))
                        }
                        // Clear any password-related errors when user focuses
                        if (errors.password) {
                          setErrors(prev => {
                            const { password: _password, ...rest } = prev;
                            return rest;
                          })
                        }
                      }}
                      onChange={(e) => {
                        // Only allow editing if verified or not in edit mode
                        if (!isEditMode || isPasswordVerified) {
                          const newValue = e.target.value
                          // If user starts typing and it's still the masked password, clear it first
                          if (isEditMode && newValue.startsWith('••••••••') && newValue.length > 8) {
                            setFormData(prev => ({ ...prev, password: newValue.slice(8) }))
                          } else {
                            setFormData(prev => ({ ...prev, password: newValue }))
                          }
                        }
                      }}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                        } ${isEditMode && !isPasswordVerified ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      placeholder={isEditMode ? (isPasswordVerified ? "Enter new password or use Generate button" : "Current password is set and secured") : "Enter password"}
                      disabled={isLoading || (isEditMode && !isPasswordVerified)}
                      readOnly={isEditMode && !isPasswordVerified}
                    />
                    {(!isEditMode || isPasswordVerified) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (isEditMode && isPasswordVerified && formData.password === '••••••••') {
                            // If trying to show password when it's still masked, provide helpful feedback
                            setErrors({
                              general: 'The current password is securely stored and cannot be displayed. You can generate a new password or enter a custom one.'
                            })
                            return
                          }
                          setShowPassword(!showPassword)
                        }}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={isLoading}
                        title={isEditMode && isPasswordVerified && formData.password === '••••••••'
                          ? "Current password is secured. Generate new password or enter custom one."
                          : showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    )}
                    {isEditMode && !isPasswordVerified && (
                      <Lock className="absolute inset-y-0 right-0 pr-3 h-4 w-4 text-gray-400" />
                    )}
                  </div>

                  {/* Password Help Text for Edit Mode */}
                  {isEditMode && isPasswordVerified && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium mb-1">Password Security Notice</p>
                          <p className="text-xs leading-relaxed mb-2">
                            The current password is securely stored and cannot be displayed for security reasons.
                            You can either generate a new secure password using the &quot;Generate&quot; button,
                            or enter a new password manually.
                          </p>

                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password Verification Modal */}
                  {showVerification && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-start space-x-3">
                        <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">
                            Password Verification Required
                          </h4>
                          <p className="text-xs text-blue-700 mb-3">
                            {isEditingSelf
                              ? "Enter your current password to verify your identity. No confirmation will be required."
                              : "Enter your admin password to modify this user's password."
                            }
                          </p>

                          <div className="space-y-2">
                            <input
                              type="password"
                              value={adminPassword}
                              onChange={(e) => setAdminPassword(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              placeholder="Enter your admin password"
                              disabled={isVerifying}
                              autoComplete="current-password"
                              key={`verification-${showVerification ? 'open' : 'closed'}`} // Force re-render when modal opens
                            />

                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                onClick={verifyAdminPassword}
                                disabled={isVerifying || !adminPassword.trim()}
                                size="sm"
                                className="text-xs"
                              >
                                {isVerifying ? 'Verifying...' : 'Verify Password'}
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                  setShowVerification(false)
                                  setAdminPassword('')
                                  setErrors({})
                                }}
                                disabled={isVerifying}
                                size="sm"
                                className="text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password Strength Checker */}
                  {passwordStrength && (!isEditMode || isPasswordVerified) && (
                    <div className="mt-2 space-y-1" role="region" aria-label="Password strength requirements">
                      <div className="flex items-center gap-2 text-xs">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${passwordStrength.strength === 'Strong' ? 'bg-green-100 text-green-800' :
                            passwordStrength.strength === 'Good' ? 'bg-blue-100 text-blue-800' :
                              passwordStrength.strength === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                            }`}
                          aria-label={`Password strength: ${passwordStrength.strength}`}
                        >
                          {passwordStrength.strength}
                        </span>
                        <span className="text-gray-600">Password strength</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1" aria-label="8+ characters requirement">
                          {passwordStrength.checks.length ?
                            <Check className="h-3 w-3 text-green-600" aria-hidden="true" /> :
                            <X className="h-3 w-3 text-red-600" aria-hidden="true" />
                          }
                          <span className={passwordStrength.checks.length ? 'text-green-700' : 'text-red-700'}>
                            8+ characters
                          </span>
                        </div>
                        <div className="flex items-center gap-1" aria-label="Uppercase letter requirement">
                          {passwordStrength.checks.uppercase ?
                            <Check className="h-3 w-3 text-green-600" aria-hidden="true" /> :
                            <X className="h-3 w-3 text-red-600" aria-hidden="true" />
                          }
                          <span className={passwordStrength.checks.uppercase ? 'text-green-700' : 'text-red-700'}>
                            Uppercase letter
                          </span>
                        </div>
                        <div className="flex items-center gap-1" aria-label="Lowercase letter requirement">
                          {passwordStrength.checks.lowercase ?
                            <Check className="h-3 w-3 text-green-600" aria-hidden="true" /> :
                            <X className="h-3 w-3 text-red-600" aria-hidden="true" />
                          }
                          <span className={passwordStrength.checks.lowercase ? 'text-green-700' : 'text-red-700'}>
                            Lowercase letter
                          </span>
                        </div>
                        <div className="flex items-center gap-1" aria-label="Number requirement">
                          {passwordStrength.checks.number ?
                            <Check className="h-3 w-3 text-green-600" aria-hidden="true" /> :
                            <X className="h-3 w-3 text-red-600" aria-hidden="true" />
                          }
                          <span className={passwordStrength.checks.number ? 'text-green-700' : 'text-red-700'}>
                            Number
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password Field */}
                {/* Hide confirm password when editing self (before AND after verification) */}
                {!isEditingSelf && (
                  <div>
                    <Label.Root htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </Label.Root>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={isEditingSelf ? '' : formData.confirmPassword} // Always empty for self-editing
                        onChange={(e) => {
                          if (!isEditingSelf) { // Only allow changes when not editing self
                            setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))
                          }
                        }}
                        className={`w-full px-3 py-2 pr-10 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                          }`}
                        placeholder={isEditingSelf ? "Hidden for self-editing" : "Confirm password"}
                        disabled={isLoading || isEditingSelf} // Disable for self-editing
                        readOnly={isEditingSelf} // Make read-only for self-editing
                      />
                      {!isEditingSelf && (
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                    )}
                  </div>
                )}

                {/* Show verification status when editing self */}
                {isEditingSelf && isPasswordVerified && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700">
                        {isEditingSelf
                          ? "Identity verified. You can now set your new password."
                          : "Password verified. You can now modify this user's password."
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="px-4 py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="min-w-[120px] px-4 py-2 bg-blue-600 hover:bg-blue-700"
                  >
                    {isLoading
                      ? (isEditMode ? 'Updating...' : 'Creating...')
                      : (isEditMode ? 'Update User' : 'Create User')
                    }
                  </Button>
                </div>
              </form>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </AdminOnly>
  )
}
